import { JSX, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog_VM,
  DialogFrame,
  useDialog,
} from "../../../components/Dialogs/dialog-base";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import LoadingElement from "../../../components/loading-element/loading-element";
import { Organization } from "../../../../core/models/entities/Organization";
import { User } from "../../../../core/models/entities/User";
import { useOrganizationRepository } from "../../../../data/repositories/OrganizationRepository";
import { PageQuery } from "../../../../utils/models/pageQuery";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../../shared/hooks/useComponentStateMachine";
import { InputWrapper } from "../../../components/Input/InputWrapper";
import { InputLabel } from "../../../components/Input/InputLabel";
import { CustomAutoComplete } from "../../../components/Input/CustomAutoComplete";
import {
  ImpersonatedUser,
  useOrganizationImpersonationContext,
} from "../../../../contexts/OrganizationImpersonationContext";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { useOrganizationUsers } from "../../../hooks/useOrganizationUsers";
import { CreateUserDialog } from "../../../components/Organization/user-list.component";
import { CreateUserDto } from "../../../../core/models/dtos/CreateUserDto";
import { useEffect } from "react";

interface Props {
  component: JSX.Element;
  open: () => void;
}

export function useAdminImpersonationDialog({
  onContinue,
  onCancel,
}: {
  onContinue: () => Promise<void>;
  onCancel?: () => void;
}): Props {
  const {
    setImpersonation,
    vendor,
    user,
    isLoading: isImpersonationLoading,
  } = useOrganizationImpersonationContext();
  const { useAllVendors } = useOrganizationRepository();
  // NOT size 0: toQueryParam omits falsy page values, and the API then
  // DEFAULTS to pageSize 25 — so the dialog silently got only the first 25
  // vendors (name-descending) out of 2000+, making most vendors unfindable
  // ("Sport 24 Holstebro" etc.). An explicit large size returns them all.
  const {
    data: vendors,
    isError,
    isLoading,
    isSuccess,
  } = useAllVendors({ page: { index: 1, size: 10000 } } as PageQuery);

  const { state, dataTransition } = useComponentStateMachine();
  dataTransition(isLoading || isImpersonationLoading, isSuccess, isError);

  const [loading, setLoading] = useState<boolean>(false);

  const dialog = useDialog();
  const navigate = useNavigate();

  function handleSubmit(organization: Organization, vendorUser: User | null) {
    setLoading(true);
    onContinue().finally(() => {
      setLoading(false);
      // No cart clearing here: CartProvider's scope-switch effect parks the
      // current identity's cart and loads the target identity's own bucket.
      // With a chosen user, order confirmations & attribution follow that
      // user, exactly as if they had logged in themselves. The null branch is
      // the USERLESS path (vendor has no webshop users — 99% of vendors): the
      // admin acts as themselves, the API omits OnBehalfOfUserId, orders are
      // attributed "admin (on behalf of org)" and mails fall back to the
      // vendor org's e-mail.
      setImpersonation(
        organization.id,
        vendorUser
          ? {
              id: vendorUser.id,
              firstName: vendorUser.firstName,
              lastName: vendorUser.lastName,
              email: vendorUser.email,
            }
          : undefined
      );
      dialog.closeDialog();
      // The identity just changed under the current route. If that route is
      // admin-gated (AuthRoute) or holds admin-scoped data, the vendor role
      // renders "not found"/errors in place — so every act-as entry (and
      // vendor→vendor switch) lands on the webshop's home instead.
      navigate("/logo");
    });
  }

  function handleClear() {
    setImpersonation(undefined, undefined);
    dialog.closeDialog();
  }

  function handleCancel() {
    if (onCancel) onCancel();
    dialog.closeDialog();
  }

  return {
    component:
      state === ComponentState.Loading ? (
        <DialogFrame dialogOption={dialog}>
          <div className="h-80">
            <LoadingElement transparent />
          </div>
        </DialogFrame>
      ) : (
        <DialogFrame dialogOption={dialog}>
          {loading && <LoadingElement transparent />}
          <CreateAdminImpersionationDialogComponent
            dialog={dialog}
            onSubmit={handleSubmit}
            onClear={handleClear}
            organization={vendor}
            impersonatedUser={user}
            organizations={vendors?.results!}
          />
        </DialogFrame>
      ),
    open: dialog.openDialog,
  };
}

interface CreateDialogComponentProps {
  dialog: Dialog_VM;
  onSubmit: (organization: Organization, vendorUser: User | null) => void;
  onClear: () => void;
  organization?: Organization;
  impersonatedUser?: ImpersonatedUser;
  organizations: Organization[];
}

export function CreateAdminImpersionationDialogComponent({
  onSubmit,
  onClear,
  organization,
  impersonatedUser,
  organizations,
}: CreateDialogComponentProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Organization | null>(
    organization ?? null
  );
  // undefined = untouched (preselect may apply), null = explicitly cleared.
  const [selectedUser, setSelectedUser] = useState<User | null | undefined>(
    undefined
  );

  // Step 2: the vendor's users — fetched lazily once a vendor is picked.
  // useOrganizationUsers also exposes creation: most vendors have no webshop
  // user, and the act-as flow requires one so order confirmations reach a real
  // person — so the admin can create the user right here (real B2C account,
  // welcome mail with login, same flow as the customer-update screen).
  const {
    users,
    onCreateUser,
    isLoading: usersLoading,
    isError: usersError,
    isSuccess: usersLoaded,
  } = useOrganizationUsers(selected?.id ?? 0, {
    enabled: selected != null,
  });
  const createUserDialog = useDialog();
  const [pendingCreatedEmail, setPendingCreatedEmail] = useState<string | null>(
    null
  );

  function handleCreateUser(dto: CreateUserDto) {
    onCreateUser(dto).then(() => {
      // The create endpoint returns no body — the invalidated ["users", orgId]
      // query refetches, and the effect below auto-selects the new user by
      // e-mail once it appears.
      setPendingCreatedEmail(dto.email);
      createUserDialog.closeDialog();
    });
  }

  useEffect(() => {
    if (!pendingCreatedEmail) return;
    const created = (users ?? []).find(
      (u) => u.email?.toLowerCase() === pendingCreatedEmail.toLowerCase()
    );
    if (created) {
      setSelectedUser(created);
      setPendingCreatedEmail(null);
    }
  }, [users, pendingCreatedEmail]);

  // When re-opening while already impersonating, preselect the active user —
  // but only while the field is untouched, so clearing it doesn't snap back.
  const effectiveUser =
    selectedUser !== undefined
      ? selectedUser
      : selected?.id === organization?.id && impersonatedUser
        ? users?.find((u) => u.id === impersonatedUser.id) ?? null
        : null;

  // A vendor is always required. A user is required only when the vendor HAS
  // users (picking a person keeps confirmations/attribution on them) — a
  // vendor with NO webshop users can be entered userless: the admin acts as
  // themselves and the API attributes the order to the vendor org. Requiring
  // a user here used to make those vendors un-actable without creating a
  // throwaway user each time.
  // "No users" must be a POSITIVE fact: the query has to have SUCCEEDED with
  // an empty list (the hook returns [] on error too, and a failed fetch must
  // never unlock userless entry for a vendor that actually has users). While
  // loading, after an error, or while a just-created user's refetch is still
  // pending (pendingCreatedEmail), the button stays locked.
  const noUsers =
    usersLoaded &&
    !usersLoading &&
    pendingCreatedEmail == null &&
    (users ?? []).length === 0;
  const isValid = selected !== null && (effectiveUser != null || noUsers);

  return (
    <div className={"flex flex-col space-y-4 p-4"}>
      <p className={"text-left text-base font-bold"}>
        {t("headerFooter.actAsVendor")}
      </p>
      <div className={"flex flex-col justify-center space-y-4"}>
        <InputWrapper>
          <InputLabel>{t("vendor")}</InputLabel>
          <CustomAutoComplete<Organization>
            options={organizations}
            getOptionKey={(org) => org.id}
            displayOption={(org) => `${org.name} (${org.navNumber})`}
            key={selected?.id}
            onChange={(org) => {
              setSelected(org);
              // A different vendor means a different user list — never keep
              // the previous vendor's user selected (back to untouched so the
              // preselect can apply if the active vendor is re-picked).
              setSelectedUser(undefined);
            }}
            value={selected}
            placeholder={t("headerFooter.searchForVendors")}
          />
        </InputWrapper>
        {selected && (
          <InputWrapper>
            <InputLabel>{t("headerFooter.selectUser")}</InputLabel>
            {usersLoading ? (
              <div className="h-10">
                <LoadingElement transparent />
              </div>
            ) : usersError ? (
              // Failed lookup is NOT "no users" — never offer userless entry
              // on an error (the vendor may well have users).
              <p className="text-sm text-red-600">
                {t("orderConfirmation.somethingWentWrong")}
              </p>
            ) : (
              <>
                {(users ?? []).length === 0 ? (
                  // No users at all (the common case for vendors that never
                  // used the webshop): the admin can continue as themselves —
                  // creating a user stays available as the alternative.
                  <p className="text-sm text-gray-500">
                    {t("headerFooter.noUsersHint")}
                  </p>
                ) : (
                  <CustomAutoComplete<User>
                    options={users ?? []}
                    getOptionKey={(u) => u.id}
                    displayOption={(u) =>
                      `${u.firstName} ${u.lastName}`.trim() +
                      (u.email ? ` (${u.email})` : "")
                    }
                    key={`${selected.id}_${effectiveUser?.id ?? "none"}`}
                    onChange={(u) => setSelectedUser(u)}
                    value={effectiveUser ?? null}
                    placeholder={t("headerFooter.searchForUsers")}
                  />
                )}
                <button
                  type="button"
                  className="mt-1 self-start text-sm font-semibold text-blue-600 hover:underline"
                  onClick={createUserDialog.openDialog}
                >
                  + {t("add_user")}
                </button>
              </>
            )}
          </InputWrapper>
        )}
        <PrimaryButton
          width={"full"}
          onClick={() => onSubmit(selected!, effectiveUser ?? null)}
          disabled={!isValid}
        >
          {t("headerFooter.actAsVendor")}
        </PrimaryButton>
        <SecondaryButton width={"full"} onClick={onClear}>
          {t("headerFooter.resetToAdmin")}
        </SecondaryButton>
      </div>
      {/* Real user creation (B2C account + welcome mail with login) — the same
          dialog the customer-update screen uses. */}
      <CreateUserDialog
        dialogOptions={createUserDialog}
        onCreate={handleCreateUser}
      />
    </div>
  );
}
